import React from 'react';
import {Box, Chip, Divider, Stack, Typography} from "@mui/material";
import {useParams, useSearchParams} from "react-router";
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import previewTheme from './previewTheme';
import VersionSelector from './VersionSelector';
import ReferencesList from './ReferencesList';
import useVersionManagement from './useVersionManagement';
import useReferenceProcessing from './useReferenceProcessing';
import {useVersionDiff} from './useVersionDiff';
import usePreviewAuthors from './usePreviewAuthors';
import ReportContent from './ReportContent';
import {ErrorState, LoadingState, NoContentState, NoVersionState} from './PreviewStates';
import UserMentionPopover from '../../../components/prosemirror-editor/components/UserMentionPopover';
import HashtagPopover from '../../../components/prosemirror-editor/components/HashtagPopover';
import {useUserMentionPopover} from '../../../components/prosemirror-editor/hooks/useUserMentionPopover';
import {useHashtagPopover} from '../../../components/prosemirror-editor/hooks/useHashtagPopover';
import {useReportExcerptScroll} from './useReportExcerptScroll';
import IirEvaluationSection from '../../collection/iirEvaluations/IirEvaluationSection';
const EMPTY_SEARCH_TERMS = [];

/**
 * Sticky column label shown above each side of the version-compare layout.
 */
const CompareColumnHeader = ({label, version}) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1,
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
    }}>
        <Typography variant="overline" color="text.secondary" sx={{fontWeight: 700}}>
            {label}
        </Typography>
        <Chip size="small" label={`v${version}`} sx={{fontFamily: 'monospace'}}/>
    </Box>
);

const ReportPreviewPage = (props) => {
    const params = useParams();
    const reportId = props?.reportId || params?.reportId;
    const hideVersionSidebar = props?.hideVersionSidebar || false;
    const showVersionSidebar = !hideVersionSidebar;
    // Optional excerpt-scroll terms (e.g. when embedded from a search hit). When empty,
    // the scroll hook is a no-op, preserving behavior for all existing callers.
    const searchTerms = props?.searchTerms || EMPTY_SEARCH_TERMS;

    // Refs used by the excerpt-scroll hook to locate and scroll to the matched text.
    const contentRef = React.useRef(null);
    const scrollContainerRef = React.useRef(null);

    // State for selected author (for highlighting)
    const [selectedAuthorId, setSelectedAuthorId] = React.useState(null);

    // User mention popover management
    const {
        anchorEl: mentionAnchorEl,
        anchorPosition: mentionAnchorPosition,
        userData: mentionUserData,
        open: mentionPopoverOpen,
        handleUserMentionClick,
        handleClose: handleMentionPopoverClose,
    } = useUserMentionPopover();

    // Hashtag popover management
    const {
        anchorEl: hashtagAnchorEl,
        anchorPosition: hashtagAnchorPosition,
        tagData: hashtagData,
        open: hashtagPopoverOpen,
        handleHashtagClick,
        handleClose: handleHashtagPopoverClose,
    } = useHashtagPopover();

    // Manage version selection and document loading
    // When embedded (hideVersionSidebar), manage version locally instead of from URL
    const {
        version,
        latestVersion,
        versions,
        reportDoc,
        headerDoc,
        content,
        references,
        meta,
        documentHeader,
        previewAuthors,
        isLoading,
        error,
        docLoading,
        versionsLoading
    } = useVersionManagement(reportId, hideVersionSidebar);

    // Version comparison: ?compareTo=NNNNNNN alongside ?version=NNNNNNN puts the preview into a
    // side-by-side diff view (base = compareTo, target = version). Block-level, text-only diff —
    // see services/ydoc-version-manager/pmDiff.js.
    const [searchParams] = useSearchParams();
    const compareTo = hideVersionSidebar ? null : searchParams.get('compareTo');
    const isComparing = !!compareTo && !!version && compareTo !== version;
    const {
        contentA: compareContent,
        referencesA: compareReferences,
        metadataA: compareMetadata,
        diffMap,
        loading: diffLoading,
        error: diffError,
    } = useVersionDiff(reportId, isComparing ? compareTo : null, isComparing ? version : null);

    // Fields like icrNumber/topic/icrNumber live only on the live report record
    // (reports.meta_json) and are never written into Yjs documentMeta, so no historical
    // version snapshot ever captures them. Without this fallback the "from" side of a
    // compare would always render them blank while the "to" side (which reads meta straight
    // off the live report) shows the current value — making it look like a real change when
    // nothing was actually diffed. Snapshot fields (title, classification, ...) still win when
    // the historical snapshot has them, so genuinely versioned changes are preserved.
    const compareMeta = React.useMemo(
        () => ({...(headerDoc?.meta || {}), ...(compareMetadata || {})}),
        [headerDoc, compareMetadata]
    );

    const {authors: comparePreviewAuthors} = usePreviewAuthors({
        content: isComparing ? compareContent : null,
        versionDoc: null,
        meta: compareMeta,
        documentHeader: undefined,
        reportDoc,
        reportId,
        version: compareTo,
        latestVersion,
    });

    // Process references for numbering and organization
    const {
        localReferences,
        referenceNumbers,
        referencesByMarkId,
        getReferenceNumber
    } = useReferenceProcessing(content, references);

    const {
        referencesByMarkId: compareReferencesByMarkId,
        referenceNumbers: compareReferenceNumbers,
        getReferenceNumber: getCompareReferenceNumber
    } = useReferenceProcessing(compareContent, compareReferences);

    // When search terms are supplied (embedded search-hit preview), scroll to the excerpt.
    useReportExcerptScroll(contentRef, scrollContainerRef, searchTerms, isLoading, Boolean(content));

    // Handle author selection for highlighting
    const handleAuthorClick = (authorId) => {
        // Toggle selection: if already selected, deselect
        setSelectedAuthorId(prev => prev === authorId ? null : authorId);
    };

    return (
        <>
            <Stack direction="row" sx={{
                flexGrow: 1, overflow: 'hidden'
            }}>
                {showVersionSidebar && (
                    <VersionSelector
                        reportId={reportId}
                        versions={versions}
                        latestVersion={latestVersion}
                        loading={docLoading || versionsLoading}
                    />
                )}

                <Box
                    ref={scrollContainerRef}
                    data-report-scroll-root
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        overflowY: 'auto',
                        bgcolor: 'white'
                    }}>
                    <ThemeProvider theme={previewTheme}>
                        <CssBaseline/>
                        <Stack
                            className="preview-root"
                            spacing={2}
                            p={2}
                            maxWidth={isComparing ? '1600px' : '800px'}
                            width={'100%'}
                            margin="0 auto"
                        >
                            {/* Loading state */}
                            {isLoading && <LoadingState/>}

                            {/* Error state */}
                            {error && !isLoading && <ErrorState error={error}/>}

                            {/* No version selected yet (waiting for redirect) */}
                            {!version && !isLoading && !error && (
                                <NoVersionState latestVersion={latestVersion}/>
                            )}

                            {/* No content available */}
                            {version && !isLoading && !error && !content && (
                                <NoContentState version={version}/>
                            )}

                            {!isLoading && !error && content && isComparing && (
                                <Stack direction={{xs: 'column', md: 'row'}} spacing={2} divider={<Divider orientation="vertical" flexItem/>}>
                                    <Box sx={{flex: 1, minWidth: 0}}>
                                        <CompareColumnHeader label="Comparing from" version={compareTo}/>
                                        {diffError && <ErrorState error={diffError}/>}
                                        {diffLoading && !compareContent && <LoadingState/>}
                                        {!diffLoading && !diffError && !compareContent && (
                                            <NoContentState version={compareTo}/>
                                        )}
                                        {compareContent && (
                                            <ReportContent
                                                content={compareContent}
                                                meta={compareMeta}
                                                previewAuthors={comparePreviewAuthors}
                                                reportDoc={headerDoc || reportDoc}
                                                version={compareTo}
                                                latestVersion={latestVersion}
                                                referencesByMarkId={compareReferencesByMarkId}
                                                referenceNumbers={compareReferenceNumbers}
                                                getReferenceNumber={getCompareReferenceNumber}
                                                diffMap={diffMap}
                                                onUserMentionClick={handleUserMentionClick}
                                                onHashtagClick={handleHashtagClick}
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{flex: 1, minWidth: 0}} ref={contentRef}>
                                        <CompareColumnHeader label="Comparing to" version={version}/>
                                        <ReportContent
                                            content={content}
                                            meta={meta}
                                            documentHeader={documentHeader}
                                            previewAuthors={previewAuthors}
                                            reportDoc={headerDoc || reportDoc}
                                            version={version}
                                            latestVersion={latestVersion}
                                            referencesByMarkId={referencesByMarkId}
                                            referenceNumbers={referenceNumbers}
                                            getReferenceNumber={getReferenceNumber}
                                            selectedAuthorId={selectedAuthorId}
                                            onAuthorClick={handleAuthorClick}
                                            onUserMentionClick={handleUserMentionClick}
                                            onHashtagClick={handleHashtagClick}
                                            diffMap={diffMap}
                                        />
                                    </Box>
                                </Stack>
                            )}

                            {/* Render document content (single-version view) */}
                            {!isLoading && !error && content && !isComparing && (
                                <Box ref={contentRef}>
                                    <ReportContent
                                        content={content}
                                        meta={meta}
                                        documentHeader={documentHeader}
                                        previewAuthors={previewAuthors}
                                        reportDoc={headerDoc || reportDoc}
                                        version={version}
                                        latestVersion={latestVersion}
                                        referencesByMarkId={referencesByMarkId}
                                        referenceNumbers={referenceNumbers}
                                        getReferenceNumber={getReferenceNumber}
                                        selectedAuthorId={selectedAuthorId}
                                        onAuthorClick={handleAuthorClick}
                                        onUserMentionClick={handleUserMentionClick}
                                        onHashtagClick={handleHashtagClick}
                                    />
                                </Box>
                            )}

                            {/* References list */}
                            {!isLoading && !error && content && (
                                <>
                                    <Divider/>
                                    <ReferencesList
                                        references={localReferences}
                                        getReferenceNumber={getReferenceNumber}
                                    />
                                </>
                            )}

                            {/* Analyst reviews — bottom of IIR */}
                            {!isLoading && !error && content && (
                                <IirEvaluationSection
                                    reportId={reportId}
                                    productType={meta?.productType}
                                    status={
                                        (headerDoc || reportDoc)?.status
                                        || meta?.workflowStatus
                                    }
                                />
                            )}
                        </Stack>
                    </ThemeProvider>
                </Box>
            </Stack>

            {/* User mention popover */}
            <UserMentionPopover
                open={mentionPopoverOpen}
                anchorEl={mentionAnchorEl}
                anchorPosition={mentionAnchorPosition}
                onClose={handleMentionPopoverClose}
                userData={mentionUserData}
            />

            {/* Hashtag popover */}
            <HashtagPopover
                open={hashtagPopoverOpen}
                anchorEl={hashtagAnchorEl}
                anchorPosition={hashtagAnchorPosition}
                onClose={handleHashtagPopoverClose}
                tagData={hashtagData}
            />
        </>

    );
};

export default ReportPreviewPage;
